import { isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, PLATFORM_ID, inject, OnInit, effect, AfterViewInit, OnDestroy } from '@angular/core';
import { PresaleDataService } from '../../services/connection-services/data.service';

interface PhaseItem {
  textKey: string;
  status: 'done' | 'check' | 'pending' | 'future';
  img?: string;
}

interface Phase {
  id: string;
  titleKey: string;
  side: 'left' | 'right';
  status: 'completed' | 'current' | 'future';
  dotColor: 'cyan' | 'green' | 'dim' | 'red';
  badgeKey?: string;
  items: PhaseItem[];
}

interface Stat {
  labelKey: string;
  labelAccent: 'green' | 'cyan' | 'muted';
  titleKey: string;
  titleLarge?: boolean;
  valueKey: string;
  valueSuffixKey?: string;
  badgeKey?: string;
  extra: 'bars' | 'badge' | 'progress';
  progress?: number;
  wide?: boolean;
  bg: string;
  numericValue?: number;
  animatedValue?: number;
  currencyFormat?: boolean;
  currencySuffix?: string;
  creadshield?: boolean;
  audit_text?: string;
}

@Component({
  selector: 'app-roadmap',
  standalone: false,
  templateUrl: './roadmap.component.html',
  styleUrl: './roadmap.component.scss',
})
export class RoadmapComponent implements OnInit, AfterViewInit, OnDestroy {
  private observer: IntersectionObserver | null = null;
  private statObserver: IntersectionObserver | null = null;
  scrollProgress = 0;
  private dataService = inject(PresaleDataService);

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const timeline = document.querySelector('.roadmap__timeline') as HTMLElement;
    if (!timeline) return;

    const rect = timeline.getBoundingClientRect();
    const viewportHeight = window.innerHeight;


    const startTrigger = viewportHeight / 2;
    const progress = (startTrigger - rect.top) / rect.height;

    // Clamp between 0 and 100
    this.scrollProgress = Math.min(Math.max(progress * 100, 0), 100);
  }
  constructor(
    private el: ElementRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    effect(() => {
      const balanceData = this.dataService.balanceData();
      // console.log('Balance data updated:', balanceData);
      if (balanceData && balanceData.evm) {
        // Update first stat - Total Tokens Sold
        if (this.stats[0]) {
          const totalTokensSold = balanceData.evm.totalTokensSold || 0;
          if (totalTokensSold && totalTokensSold > 0) {
            this.stats[0].numericValue = Math.floor(Number(totalTokensSold));
            this.stats[0].animatedValue = 0;
            // Trigger animation immediately
            this.animateCounter(this.stats[0]);
            // console.log('Updated stat 1 with totalTokensSold:', totalTokensSold);
          }
        }

        // Update second stat - Total USD Raised (in millions)

        if (this.stats[1]) {
          const totalUsdRaised = balanceData.evm.totalUsdRaised || 0;
          //  console.log('totalUsdRaisedtotalUsdRaisedtotalUsdRaised', totalUsdRaised);
          if (totalUsdRaised && totalUsdRaised > 0) {
            this.stats[1].numericValue = Number(totalUsdRaised);
            this.stats[1].animatedValue = 0;
            // console.log('Total USDT Raised:', totalUsdRaised);
            // Trigger animation immediately
            this.animateCounter(this.stats[1]);
            // console.log('Updated stat 2 with totalUsdRaised:', totalUsdRaised);
          }
        }
      }
    });
  }

  ngOnInit() {
    // Use effect to reactively update stats when balance data changes

  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Small timeout ensures Angular has finished rendering the @for loops
      setTimeout(() => {
        this.initScrollObserver();
        this.initStatObserver();
      }, 500);
    }
  }

  private initScrollObserver() {
    const options = {
      root: null,
      // '0px' margin makes it trigger as soon as the card touches the viewport
      rootMargin: '-10% 0px -30% 4px',
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Log to console so you can see if it's working
        // console.log('Target:', entry.target, 'Is Intersecting:', entry.isIntersecting);

        const dot = entry.target.querySelector('.roadmap__dot');
        if (dot) {
          if (entry.isIntersecting) {
            dot.classList.add('is-active');
          } else {
            dot.classList.remove('is-active');
          }
        }
      });
    }, options);

    // Grab all entry elements
    const entries = this.el.nativeElement.querySelectorAll('.roadmap__entry');

    if (entries.length === 0) {
      // console.warn('Roadmap entries not found! Check your HTML class names.');
    }

    entries.forEach((element: HTMLElement) => {
      this.observer?.observe(element);
    });
  }

  private initStatObserver() {
    const options = {
      root: null,
      rootMargin: '-10% 0px -10% 0px',
      threshold: 0.5
    };

    this.statObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target as HTMLElement;
          const index = Array.from(this.el.nativeElement.querySelectorAll('.roadmap__stat')).indexOf(element);
          if (index !== -1 && this.stats[index] && this.stats[index].numericValue) {
            this.animateCounter(this.stats[index]);
            this.statObserver?.unobserve(element); // Only animate once
          }
        }
      });
    }, options);

    // Observe all stat elements
    const statElements = this.el.nativeElement.querySelectorAll('.roadmap__stat');
    statElements.forEach((element: HTMLElement) => {
      this.statObserver?.observe(element);
    });
  }

  private animateCounter(stat: Stat) {
    if (!stat.numericValue) return;

    const duration = 1500; // 1.5 seconds
    const startValue = 0;
    const endValue = stat.numericValue;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      stat.animatedValue = Math.floor(startValue + (endValue - startValue) * progress);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        stat.animatedValue = endValue;
      }
    };

    animate();
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.statObserver) {
      this.statObserver.disconnect();
    }
  }

  phases: Phase[] = [
    {
      id: '01',
      titleKey: 'roadmap.q1_title',
      side: 'left',
      status: 'completed',
      dotColor: 'cyan',
      items: [
        { textKey: 'roadmap.q1_item1', status: 'done', img: '/assets/images/svg-icons/check-icon.svg' },
        { textKey: 'roadmap.q1_item2', status: 'done', img: '/assets/images/svg-icons/check-icon.svg' },
        { textKey: 'roadmap.q1_item3', status: 'done', img: '/assets/images/svg-icons/check-icon.svg' },
        { textKey: 'roadmap.q1_item4', status: 'done', img: '/assets/images/svg-icons/check-icon.svg' },
        { textKey: 'roadmap.q1_item5', status: 'done', img: '/assets/images/svg-icons/check-icon.svg' },
      ],
    },
    {
      id: '02',
      titleKey: 'roadmap.q2_title',
      side: 'right',
      status: 'current',
      dotColor: 'green',
      badgeKey: 'roadmap.q2_badge',
      items: [
        { textKey: 'roadmap.q2_item1', status: 'check', img: '/assets/images/svg-icons/refresh-icon.svg' },
        { textKey: 'roadmap.q2_item2', status: 'pending', img: '/assets/images/svg-icons/round-icon.svg' },
        { textKey: 'roadmap.q2_item3', status: 'pending', img: '/assets/images/svg-icons/round-icon.svg' },
        { textKey: 'roadmap.q2_item4', status: 'pending', img: '/assets/images/svg-icons/round-icon.svg' },
        { textKey: 'roadmap.q2_item5', status: 'pending', img: '/assets/images/svg-icons/round-icon.svg' },
        { textKey: 'roadmap.q2_item6', status: 'pending', img: '/assets/images/svg-icons/round-icon.svg' },
      ],
    },
    {
      id: '03',
      titleKey: 'roadmap.q3_title',
      side: 'left',
      status: 'future',
      dotColor: 'dim',
      items: [
        // { textKey: 'roadmap.q3_item1', status: 'future', img: '/assets/images/svg-icons/lock-icon.svg' },
        { textKey: 'roadmap.q3_item2', status: 'future', img: '/assets/images/svg-icons/lock-icon.svg' },
        { textKey: 'roadmap.q3_item3', status: 'future', img: '/assets/images/svg-icons/lock-icon.svg' },
        { textKey: 'roadmap.q3_item4', status: 'future', img: '/assets/images/svg-icons/lock-icon.svg' },
        { textKey: 'roadmap.q3_item5', status: 'future', img: '/assets/images/svg-icons/lock-icon.svg' },
      ],
    },
    {
      id: '04',
      titleKey: 'roadmap.q4_title',
      side: 'right',
      status: 'future',
      dotColor: 'red',
      items: [
        { textKey: 'roadmap.q4_item1', status: 'future', img: '/assets/images/svg-icons/multi-icon.svg' },
        { textKey: 'roadmap.q4_item2', status: 'future', img: '/assets/images/svg-icons/tree-icon.svg' },
        { textKey: 'roadmap.q4_item3', status: 'future', img: '/assets/images/svg-icons/chart-icon.svg' },
        { textKey: 'roadmap.q4_item4', status: 'future', img: '/assets/images/svg-icons/multi-icon.svg' },
        { textKey: 'roadmap.q4_item5', status: 'future', img: '/assets/images/svg-icons/tree-icon.svg' },
        { textKey: 'roadmap.q4_item6', status: 'future', img: '/assets/images/svg-icons/chart-icon.svg' },
      ],
    },
    {
      id: '05',
      titleKey: 'roadmap.q5_title',
      side: 'left',
      status: 'future',
      dotColor: 'red',
      items: [
        { textKey: 'roadmap.q5_item1', status: 'future', img: '/assets/images/svg-icons/multi-icon.svg' },
        { textKey: 'roadmap.q5_item2', status: 'future', img: '/assets/images/svg-icons/tree-icon.svg' },
        { textKey: 'roadmap.q5_item3', status: 'future', img: '/assets/images/svg-icons/chart-icon.svg' },
        { textKey: 'roadmap.q5_item4', status: 'future', img: '/assets/images/svg-icons/multi-icon.svg' },
      ],
    },
  ];

  stats: Stat[] = [
    {
      labelKey: 'roadmap.stat1_label',
      labelAccent: 'green',
      titleKey: 'roadmap.stat1_title',
      valueKey: 'roadmap.stat1_value',
      extra: 'bars',
      bg: '#18181b',
      numericValue: 1402,
      animatedValue: 0,
    },
    {
      labelKey: 'roadmap.stat2_label',
      labelAccent: 'muted',
      titleKey: 'roadmap.stat2_title',
      valueKey: 'roadmap.stat2_value',
      badgeKey: 'roadmap.stat2_change',
      extra: 'badge',
      bg: '#262626',
      numericValue: 0,
      animatedValue: 0,
      currencyFormat: true,
      currencySuffix: '',
    },
    {
      labelKey: 'misc.audit',
      labelAccent: 'cyan',
      titleKey: '',
      titleLarge: true,
      valueKey: '',
      valueSuffixKey: '',
      extra: 'progress',
      progress: 98,
      wide: true,
      bg: '#171717',
      numericValue: 10,
      animatedValue: 0,
      creadshield: true,
      audit_text: 'roadmap.audit_text'
    },
  ];



}
